import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, Clock, Lock, Loader, UtensilsCrossed, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { getMenusWeek, seleccionar } from '../../api/cafeteria';

// ── helpers de fechas ──────────────────────────────────────────────────────

function toISO(date) {
  return date.toLocaleDateString('sv-SE');
}

function getMondayOfWeek(refDate = new Date()) {
  const d = new Date(refDate);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function buildWeekDays(monday) {
  const labels = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return labels.map((label, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return { label, fecha: toISO(d), date: d };
  });
}

function horaLimiteDisplay(hora_limite) {
  if (!hora_limite) return '';
  const [h, m] = hora_limite.split(':');
  const d = new Date();
  d.setHours(parseInt(h), parseInt(m), 0, 0);
  return d.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' });
}

function pasaronDeadline(fecha, hora_limite) {
  if (!hora_limite) return false;
  const [h, m] = hora_limite.split(':');
  const limit = new Date(fecha + 'T00:00:00');
  limit.setHours(parseInt(h), parseInt(m), 0, 0);
  return new Date() >= limit;
}

// ── componente principal ───────────────────────────────────────────────────

export default function EmpleadoMenu() {
  const [monday, setMonday] = useState(() => getMondayOfWeek());
  const [weekMenus, setWeekMenus] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [messages, setMessages] = useState({});

  const weekDays = buildWeekDays(monday);

  const loadWeek = useCallback(async () => {
    setLoading(true);
    try {
      const lunes = toISO(monday);
      const data = await getMenusWeek(lunes);
      const map = {};
      for (const m of (data.menus || [])) {
        const key = (m.fecha || '').split('T')[0];
        map[key] = m;
      }
      setWeekMenus(map);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [monday]);

  useEffect(() => { loadWeek(); }, [loadWeek]);

  const prevWeek = () => {
    const d = new Date(monday);
    d.setDate(d.getDate() - 7);
    setMonday(d);
  };

  const nextWeek = () => {
    const d = new Date(monday);
    d.setDate(d.getDate() + 7);
    setMonday(d);
  };

  const handleSeleccionar = async (menu, opcionId) => {
    if (saving) return;
    setSaving(menu.id);
    setMessages(m => ({ ...m, [menu.id]: null }));
    try {
      await seleccionar(menu.id, opcionId);
      const esCambio = !!menu.miSeleccion;
      setMessages(m => ({ ...m, [menu.id]: { type: 'success', text: esCambio ? 'Selección actualizada' : '¡Selección registrada!' } }));
      await loadWeek();
    } catch (e) {
      setMessages(m => ({ ...m, [menu.id]: { type: 'error', text: e.response?.data?.error || 'Error al seleccionar' } }));
    } finally {
      setSaving(null);
    }
  };

  const saturday = weekDays[5];
  const mondayLabel = monday.toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
  const saturdayLabel = saturday.date.toLocaleDateString('es-DO', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="space-y-4">
      {/* Navegación de semana */}
      <div className="bg-white rounded-2xl shadow p-4 flex items-center justify-between">
        <button onClick={prevWeek}
          className="flex items-center gap-1 px-3 py-2 rounded-xl hover:bg-gray-100 text-gray-600 font-semibold text-sm transition-all">
          <ChevronLeft className="w-4 h-4" /> Anterior
        </button>
        <div className="text-center">
          <p className="font-bold text-gray-800">Semana del {mondayLabel} al {saturdayLabel}</p>
          <p className="text-xs text-gray-400 mt-0.5">Selecciona tu menú para cada día</p>
        </div>
        <button onClick={nextWeek}
          className="flex items-center gap-1 px-3 py-2 rounded-xl hover:bg-gray-100 text-gray-600 font-semibold text-sm transition-all">
          Siguiente <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse">
              {/* Encabezado: un th por día */}
              <thead>
                <tr>
                  {weekDays.map(({ label, fecha, date }) => {
                    const isToday = toISO(new Date()) === fecha;
                    const menu = weekMenus[fecha] || null;
                    const puedeSeleccionar = menu?.estado === 'published' && !pasaronDeadline(fecha, menu?.hora_limite);

                    return (
                      <th key={fecha}
                        className={`border-b border-r border-gray-100 p-3 text-left last:border-r-0 w-[16.66%] ${isToday ? 'bg-orange-50' : 'bg-gray-50'}`}>
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold mb-1.5 ${
                          isToday ? 'bg-orange-500 text-white' : 'bg-white text-gray-700 border border-gray-200'
                        }`}>
                          {date.getDate()}
                        </div>
                        <p className={`text-sm font-bold ${isToday ? 'text-orange-700' : 'text-gray-700'}`}>
                          {label}
                          {isToday && <span className="text-[10px] font-normal text-orange-400 ml-1">• Hoy</span>}
                        </p>
                        <p className="text-[11px] text-gray-400">
                          {date.toLocaleDateString('es-DO', { month: 'short', day: 'numeric' })}
                        </p>
                        {menu && (
                          <div className="mt-1.5">
                            {puedeSeleccionar ? (
                              <span className="flex items-center gap-1 text-[10px] text-orange-600 font-semibold">
                                <Clock className="w-3 h-3" /> Límite {horaLimiteDisplay(menu.hora_limite)}
                              </span>
                            ) : menu.estado === 'published' ? (
                              <span className="flex items-center gap-1 text-[10px] text-red-500 font-semibold">
                                <Lock className="w-3 h-3" /> Tiempo agotado
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-[10px] text-gray-400 font-semibold">
                                <Lock className="w-3 h-3" /> Cerrado
                              </span>
                            )}
                            {menu.miSeleccion && (
                              <span className="flex items-center gap-1 text-[10px] text-green-600 font-semibold mt-0.5">
                                <CheckCircle className="w-3 h-3" /> Seleccionado
                              </span>
                            )}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {/* Cuerpo: una fila, cada celda = opciones del día */}
              <tbody>
                <tr>
                  {weekDays.map(({ fecha }) => {
                    const menu = weekMenus[fecha] || null;
                    const isToday = toISO(new Date()) === fecha;
                    const puedeSeleccionar = menu?.estado === 'published' && !pasaronDeadline(fecha, menu?.hora_limite);
                    const msg = menu ? messages[menu.id] : null;
                    const isSaving = menu && saving === menu.id;

                    return (
                      <td key={fecha}
                        className={`border-r border-gray-100 p-3 align-top last:border-r-0 ${isToday ? 'bg-orange-50/20' : ''}`}>
                        {!menu ? (
                          <div className="flex flex-col items-center justify-center py-10 text-gray-200">
                            <UtensilsCrossed className="w-6 h-6 mb-1" />
                            <p className="text-xs">Sin menú</p>
                          </div>
                        ) : (
                          <div className="space-y-2.5">
                            {/* Mensaje de éxito/error */}
                            {msg && (
                              <div className={`p-2 rounded-lg text-[11px] flex items-center gap-1.5 ${
                                msg.type === 'success'
                                  ? 'bg-green-50 text-green-700 border border-green-100'
                                  : 'bg-red-50 text-red-700 border border-red-100'
                              }`}>
                                {msg.type === 'success'
                                  ? <CheckCircle className="w-3 h-3 shrink-0" />
                                  : <AlertCircle className="w-3 h-3 shrink-0" />}
                                {msg.text}
                              </div>
                            )}

                            {/* Tarjeta por opción */}
                            {[...(menu.opciones || [])].sort((a, b) => (a.numero_opcion || 1) - (b.numero_opcion || 1)).map(op => {
                              const esMiSeleccion = menu.miSeleccion?.opcion_id === op.id;
                              const sinCupos = op.cupos_maximos !== null && op.cupos_usados >= op.cupos_maximos && !esMiSeleccion;
                              const disabled = !puedeSeleccionar || sinCupos || isSaving;

                              return (
                                <div key={op.id} className={`rounded-xl border-2 p-2.5 transition-all ${
                                  esMiSeleccion
                                    ? 'border-orange-400 bg-orange-50'
                                    : sinCupos
                                    ? 'border-gray-100 bg-gray-50 opacity-50'
                                    : puedeSeleccionar
                                    ? 'border-gray-100 hover:border-orange-300 hover:shadow-sm'
                                    : 'border-gray-100 bg-gray-50/50'
                                }`}>
                                  {/* Número de opción */}
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[10px] font-bold text-orange-500 uppercase tracking-wide">
                                      Opción {op.numero_opcion || 1}
                                    </span>
                                    {esMiSeleccion && <CheckCircle className="w-3.5 h-3.5 text-orange-500" />}
                                  </div>

                                  {/* Componentes del menú */}
                                  <div className="space-y-0.5 text-[11px] text-gray-700 mb-2">
                                    {op.principal       && <p>🍚 {op.principal}</p>}
                                    {op.proteina        && <p>🍗 {op.proteina}</p>}
                                    {op.acompanamiento1 && <p>🫘 {op.acompanamiento1}</p>}
                                    {op.acompanamiento2 && <p>🥔 {op.acompanamiento2}</p>}
                                    {op.ensalada        && <p>🥬 {op.ensalada}</p>}
                                    {op.bebida          && <p>🥤 {op.bebida}</p>}
                                    {op.postre          && <p>🍰 {op.postre}</p>}
                                    {!op.principal && !op.proteina && op.nombre && (
                                      <p className="font-medium">{op.nombre}</p>
                                    )}
                                  </div>

                                  {/* Badges de precio y cupos */}
                                  <div className="flex items-center gap-1 flex-wrap mb-2">
                                    {op.precio && (
                                      <span className="text-[10px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded-full">
                                        RD${parseFloat(op.precio).toFixed(2)}
                                      </span>
                                    )}
                                    {op.cupos_maximos && !sinCupos && (
                                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                                        {op.cupos_maximos - op.cupos_usados} cupos
                                      </span>
                                    )}
                                    {sinCupos && (
                                      <span className="text-[10px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full font-semibold">
                                        Sin cupos
                                      </span>
                                    )}
                                  </div>

                                  {/* Botón seleccionar (solo si el menú está abierto) */}
                                  {puedeSeleccionar && !sinCupos && (
                                    <button
                                      onClick={() => handleSeleccionar(menu, op.id)}
                                      disabled={disabled}
                                      className={`w-full py-1.5 rounded-lg text-[11px] font-bold transition-all disabled:opacity-50 ${
                                        esMiSeleccion
                                          ? 'bg-orange-500 text-white hover:bg-orange-600'
                                          : 'bg-gray-100 text-gray-700 hover:bg-orange-100 hover:text-orange-700'
                                      }`}>
                                      {isSaving ? '...' : esMiSeleccion ? '✓ Seleccionado' : 'Seleccionar'}
                                    </button>
                                  )}

                                  {/* Indicador cuando el menú cerró y tiene tu selección */}
                                  {!puedeSeleccionar && esMiSeleccion && (
                                    <div className="w-full py-1.5 rounded-lg text-[11px] font-bold text-center bg-orange-100 text-orange-700">
                                      ✓ Tu selección
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
